import { useEffect, useRef, useState } from "react";
import API from "../../../services/api";
import { formatDateWithTimeYear } from "../../../utils/dateFormat";
import { Toast } from "../../../shared/Toast";
import { getSocket } from "../../../services/socket";
import UploadModal from "./UploadModal";
import AdTypeRequestBanner from "./AdTypeRequestBanner";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";
import { roles } from "../../../utils/constants";

const Me = localStorage.getItem("name");

const initials = (n = "") =>
  n.split(" ").map((w) => w[0]).join("").toUpperCase();

const escapeRegExp = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Chat attachments are reference files shared in the conversation — they never
// submit a design. Mirrors the allow-list enforced by /upload/alphaChatAttachment.
const ATTACHMENT_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "mp4", "mov", "webm"];

// Wrap occurrences of `query` in <mark> for search highlighting.
const highlightText = (text = "", query = "") => {
  if (!query) return text;
  const parts = String(text).split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-300 text-black rounded px-0.5">{part}</mark>
    ) : (
      part
    )
  );
};

const Message = ({ msg, prev, messages, onReply, onEdit, onDelete, onJumpToMessage, isEditing, searchQuery = "", isActiveMatch = false, isJumpTarget = false, setRef, locked = false }) => {
  const me = msg.activity_done_by_name === Me;
  const deleted = Number(msg.is_active) === 0;
  const grouped =
    prev?.activity_done_by_name === msg.activity_done_by_name &&
    formatDateWithTimeYear(prev?.date) === formatDateWithTimeYear(msg?.date);

  // `title` is activity_type — for workflow rows it IS the content ("client gave a
  // final creative"), so it stays the fallback. For chat rows it is only a routing
  // label, never content, so an attachment sent without a caption must render no
  // text body rather than the "Message - (creative name: …)" boilerplate.
  const bodyText = msg.message || (/^Message - /.test(msg.title || "") ? "" : msg.title);

  return (
    <div ref={setRef} className={`flex ${me ? "justify-end" : "justify-start"} ${grouped ? "mt-1" : "mt-4"}`}>
      {!me && (
        <div className="w-8 h-8 mr-2 flex-shrink-0">
          {!grouped && (
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold">
              {initials(msg.activity_done_by_name)}
            </div>
          )}
        </div>
      )}

      {/* `isJumpTarget` gets its own background flash (WhatsApp-style), decoupled from
          the search-match ring below: a 2px ring on a bubble already tinted green/white
          is too subtle to notice on a quick jump. `transition-colors` is what makes the
          flash fade back out smoothly once `isJumpTarget` clears, instead of cutting off. */}
      <div className={`max-w-[72%] px-3 py-2 rounded-2xl shadow-sm transition-colors duration-700 ${isJumpTarget ? "bg-amber-200" : deleted ? "bg-gray-100 border border-gray-200" : me ? "bg-[#dcf8c6]" : "bg-white border border-gray-100"} ${isEditing ? "ring-2 ring-blue-400" : ""} ${isActiveMatch ? "ring-2 ring-yellow-400" : ""} text-left`}>
        {!grouped && (
          <div className="flex justify-between gap-4 mb-1">
            <span className="text-sm font-semibold text-blue-700">{me ? "You" : msg.activity_done_by_name}</span>
            <span className="text-xs text-gray-400">{formatDateWithTimeYear(msg.date)}</span>
          </div>
        )}

        {deleted && (
          <div className="flex items-center gap-1.5 text-base italic text-gray-500 py-0.5">
            <SlIcon library="lucide" name="ban" className="text-gray-400" />
            <span>{me ? "You deleted this message" : `This message was deleted by ${msg.activity_done_by_name}`}</span>
          </div>
        )}

        {!deleted && (<>
        {msg.reply_to && (
          <div
            onClick={() => onJumpToMessage(msg.reply_to)}
            className="mb-1 px-2 py-1 bg-gray-200 border-l-2 border-green-500 rounded text-sm text-gray-600 cursor-pointer hover:bg-gray-300 transition-colors"
            title="Jump to message"
          >
            <span className="font-semibold text-green-600">Replying to:</span> {messages.find((m) => m.id === msg.reply_to)?.message?.slice(0, 50)}{messages.find((m) => m.id === msg.reply_to)?.message?.length > 50 ? "..." : ""}
          </div>
        )}

        {bodyText && (
          <div className="text-2xl break-words whitespace-pre-wrap">
            {searchQuery ? highlightText(bodyText, searchQuery) : bodyText}
          </div>
        )}

        {msg.img_url && (() => {
          const ext = msg.img_url.split("?")[0].split(".").pop().toLowerCase();
          const isVideo = ["mp4", "webm", "ogg", "mov", "avi"].includes(ext);
          const isPDF = ext === "pdf";
          return (
            <div className="mt-2">
              {isVideo ? (
                <video src={msg.img_url} controls className="rounded-lg max-h-48 max-w-full border" />
              ) : isPDF ? (
                <a
                  href={msg.img_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-colors"
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                  </svg>
                  <span className="text-base font-medium truncate max-w-[160px]">
                    {msg.img_url.split("/").pop().split("?")[0] || "PDF Document"}
                  </span>
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <img
                  src={msg.img_url}
                  alt="attachment"
                  onClick={() => window.open(msg.img_url, "_blank")}
                  className="rounded-lg max-h-40 max-w-full cursor-pointer border hover:opacity-90 object-cover"
                />
              )}
            </div>
          );
        })()}

        {/* Message Actions */}
        {!locked && me && (
          <div className="flex gap-2 mt-2 justify-end">
            <button
              onClick={() => onReply(msg)}
              className="text-[10px] text-gray-500 hover:text-blue-600 flex items-center gap-0.5"
              title="Reply"
            >
              <SlIcon library="lucide" name="reply" />
            </button>
            {!msg.img_url && (
              <button
                onClick={() => onEdit(msg)}
                className="text-[10px] text-gray-500 hover:text-green-600 flex items-center gap-0.5"
                title="Edit"
              >
                <SlIcon library="lucide" name="pencil" />
              </button>
            )}
            <button
              onClick={() => onDelete(msg.id)}
              className="text-[10px] text-gray-500 hover:text-red-600 flex items-center gap-0.5"
              title="Delete"
            >
              <SlIcon library="lucide" name="trash-2" />
            </button>
          </div>
        )}
        {!locked && !me && (
          <div className="flex gap-2 mt-2 justify-end">
            <button
              onClick={() => onReply(msg)}
              className="text-[10px] text-gray-500 hover:text-blue-600 flex items-center gap-0.5"
              title="Reply"
            >
              <SlIcon library="lucide" name="reply" />
            </button>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
};

const ChatContent = ({ data }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const textareaRef = useRef(null);
  const creative_id = data?.creative_id;

  // Reply, Edit states
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);

  // Design upload modal (upload button)
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Staged chat attachment (paperclip or paste) — a reference file, NOT a design
  const [attachment, setAttachment] = useState(null); // { file, previewUrl, isVideo }
  const [uploadProgress, setUploadProgress] = useState(null); // 0-100 while uploading, else null
  const fileInputRef = useRef(null);

  // Message search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const messageRefs = useRef({});
  const searchInputRef = useRef(null);

  // Highlight target when jumping to a replied-to message via the reply quote.
  const [jumpTargetId, setJumpTargetId] = useState(null);

  // Debounce the query so typing stays snappy on long threads.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 120);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Ad-type change request (CST only) — resolved locally after accept/reject
  const [requestResolved, setRequestResolved] = useState(false);
  const isCST = localStorage.getItem("role") === roles.ops;
  // Once the order is created (status 8) the whole conversation is locked —
  // no sending, editing, deleting or uploading is allowed.
  const isLocked = Number(data?.status) === 8;
  // Designers may upload a design only while Picked (3), Completed (4) or Rejected (5).
  const canDesignerUpload = [3, 4, 5].includes(Number(data?.status));
  const isPendingRequest = Number(data?.status) === -1;
  const pendingLog =
    data?.pending_log || messages.find((m) => Number(m.status) === -1) || null;
  const showAdTypeRequest = isCST && isPendingRequest && !requestResolved;

  const socket = getSocket();

  useEffect(() => {
    socket.emit('joinCreativeRoom', creative_id);

    const handleNewMessage = (msg) => {
      setMessages(prev => prev.some(m => m.id === msg.log.id) ? prev : [...prev, msg.log]);
    };
    const handleMessageDeleted = (msg) => {
      setMessages(prev => prev.map(m => m.id === msg.log_id ? { ...m, is_active: 0 } : m));
    };
    const handleMessageUpdated = (msg) => {
      setMessages(prev => prev.map(m => m.id === msg?.log?.id ? { ...m, ...msg.log } : m));
    };

    socket.on("newLogMessage", handleNewMessage);
    socket.on("logMessageDeleted", handleMessageDeleted);
    socket.on("logMessageUpdated", handleMessageUpdated);

    return () => {
      socket.off("newLogMessage", handleNewMessage);
      socket.off("logMessageDeleted", handleMessageDeleted);
      socket.off("logMessageUpdated", handleMessageUpdated);
      socket.emit('leaveCreativeRoom', creative_id);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creative_id]);

  // Block body on purpose: a concise arrow would return scrollIntoView()'s value,
  // and React invokes any non-undefined effect return as the cleanup function.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- Message search ---
  const trimmedQuery = debouncedQuery.toLowerCase();
  const matchIds = trimmedQuery
    ? messages
        .filter(
          (m) =>
            Number(m.is_active) !== 0 &&
            (m.message || m.title || "").toLowerCase().includes(trimmedQuery)
        )
        .map((m) => m.id)
    : [];
  const matchIdSet = new Set(matchIds);
  const activeMatchId = matchIds[currentMatchIndex];

  const registerMessageRef = (id, el) => {
    if (el) messageRefs.current[id] = el;
    else delete messageRefs.current[id];
  };

  const jumpToMessage = (id) => {
    const el = messageRefs.current[id];
    if (!el) return Toast("info", "Original message not found");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setJumpTargetId(id);
    setTimeout(() => setJumpTargetId((cur) => (cur === id ? null : cur)), 2200);
  };

  // Reset to the first match whenever the query (or result set) changes.
  useEffect(() => {
    setCurrentMatchIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Scroll the active match into view.
  useEffect(() => {
    if (activeMatchId == null) return;
    messageRefs.current[activeMatchId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMatchId, currentMatchIndex]);

  const gotoNextMatch = () => {
    if (!matchIds.length) return;
    setCurrentMatchIndex((i) => (i + 1) % matchIds.length);
  };
  const gotoPrevMatch = () => {
    if (!matchIds.length) return;
    setCurrentMatchIndex((i) => (i - 1 + matchIds.length) % matchIds.length);
  };
  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setDebouncedQuery("");
    setCurrentMatchIndex(0);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.shiftKey ? gotoPrevMatch() : gotoNextMatch();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
    }
  };

  // Single funnel for staged attachments so paperclip and paste behave identically.
  // Nothing is uploaded here — sending is explicit (Enter or the send button).
  const stageAttachment = (file) => {
    if (!file) return;
    const isVideo = (file.type || "").startsWith("video/");
    const isImage = (file.type || "").startsWith("image/");
    const ext = (file.name || "").split(".").pop().toLowerCase();
    if ((!isVideo && !isImage) || !ATTACHMENT_EXTS.includes(ext)) {
      Toast("info", `Only ${ATTACHMENT_EXTS.join(", ")} files can be attached`);
      return;
    }
    setAttachment({ file, previewUrl: URL.createObjectURL(file), isVideo });
  };

  // Cleanup object URL when attachment changes or unmounts
  useEffect(() => {
    return () => { if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl); };
  }, [attachment]);

  // Global paste listener — works without the input being focused (like WhatsApp)
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Conversation is locked once the order is created — ignore pastes.
      if (Number(data?.status) === 8) return;
      const activeEl = document.activeElement;
      // If a different input/textarea is focused, let it handle paste natively
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') &&
        activeEl !== textareaRef.current
      ) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        // Text paste → directly into textarea
        if (item.kind === 'string' && item.type === 'text/plain') {
          e.preventDefault();
          item.getAsString((pastedText) => {
            setText(prev => prev + pastedText);
            textareaRef.current?.focus();
          });
          return;
        }
        // Image/video paste → staged as a chat ATTACHMENT (never a design submission)
        if (item.kind === 'file' && (item.type.startsWith('image/') || item.type.startsWith('video/'))) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) return;
          const ext = (file.type.split('/')[1] || '').toLowerCase();
          stageAttachment(new File([file], `paste-${Date.now()}.${ext}`, { type: file.type }));
          return;
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, []);

  const markAsRead = async () => {
    try {
      await API({ url: "/alpha_cst_designer/mark_as_read", method: "POST", data: { creative_id } });
      data.unread_count = 0;
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await API({ url: "/alpha_cst_designer/getactions", method: "POST", data: { creative_id } });
      setMessages(res.status === "SUCCESS" ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (creative_id) {
      fetchMessages();
      markAsRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creative_id]);

  // Send the staged attachment + caption as ONE chat message. This hits the
  // chat-attachment endpoint, NOT the design-submission endpoints — it never
  // overwrites latest_design/video_urls and never changes the creative status.
  const uploadAttachment = async () => {
    const formData = new FormData();
    formData.append("files", attachment.file);
    formData.append("creative_id", creative_id);
    formData.append("creative_name", data.creative_name);
    formData.append("message", text.trim());

    setUploadProgress(0);
    try {
      const res = await API({
        url: "/upload/alphaChatAttachment",
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });

      if (res.status === "SUCCESS") {
        socket.emit('newLogMessage', { creative_id, log: res.data?.log[0] });
        cancelAttachment();
        setText("");
        return true;
      }
      Toast("error", res.data?.message || "Failed to send attachment");
      return false;
    } finally {
      setUploadProgress(null);
    }
  };

  const sendMessage = async () => {
    if (isLocked) return Toast("info", "Order created — this conversation is locked");
    if (!text.trim() && !attachment) return Toast("info", "Enter a message");
    setSending(true);
    try {
      if (editingMessage) {
        // Edit existing message
        const res = await API({
          url: "/alpha_cst_designer/editMessage",
          method: "POST",
          data: { id: editingMessage.id, message: text, creative_id },
        });
        if (res.status === "SUCCESS") {
          setText("");
          setEditingMessage(null);
          setMessages(prev => prev.map(m => m.id === editingMessage.id ? { ...m, message: text } : m));
          socket.emit("logMessageUpdated", { creative_id, log: res.data?.log[0] });
        } else {
          Toast("error", "Failed to edit");
        }
        return;
      }

      if (attachment) {
        await uploadAttachment();
        return;
      }

      const payload = { creative_id, creative_name: data.creative_name, message: text };
      if (replyTo) {
        payload.reply_to = replyTo.id;
      }

      const res = await API({
        url: "/alpha_cst_designer/postActions",
        method: "POST",
        data: payload,
      });
      if (res.status === "SUCCESS") {
        setText("");
        setReplyTo(null);
        socket.emit('newLogMessage', { creative_id, log: res.data?.log[0] });
      } else {
        Toast("error", "Failed to send");
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReply = (msg) => {
    setReplyTo(msg);
    setEditingMessage(null);
    textareaRef.current?.focus();
  };

  const handleEdit = (msg) => {
    setEditingMessage(msg);
    setReplyTo(null);
    setText(msg.message);
    textareaRef.current?.focus();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      const res = await API({
        url: "/alpha_cst_designer/deleteMessage",
        method: "POST",
        data: { id, creative_id },
      });
      if (res.status === "SUCCESS") {
        Toast("success", "Message deleted");
        setMessages(prev => prev.map(m => m.id === id ? { ...m, is_active: 0 } : m));
        socket.emit("logMessageDeleted", { creative_id, log_id: id });
      } else {
        Toast("error", "Failed to delete");
      }
    } catch (err) {
      Toast("error", err.message || "Failed to delete");
    }
  };

  const cancelReply = () => setReplyTo(null);
  const cancelEdit = () => {
    setEditingMessage(null);
    setText("");
  };

  const cancelAttachment = () => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
  };

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-[#ece5dd] overflow-hidden">
      {showAdTypeRequest && (
        <AdTypeRequestBanner
          creative={data}
          pendingLog={pendingLog}
          onResolved={() => setRequestResolved(true)}
        />
      )}
      {/* Search bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#f0f0f0] border-b border-gray-300 flex-shrink-0">
        {searchOpen ? (
          <>
            <div className="flex items-center gap-1.5 flex-1 min-w-0 bg-white rounded-full px-3 py-1 shadow-sm ring-1 ring-gray-200 focus-within:ring-sky-400 transition">
              <SlIcon library="lucide" name="search" className="text-gray-400 flex-shrink-0" style={{ fontSize: "17px" }} />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search messages…"
                className="flex-1 min-w-0 bg-transparent text-base focus:outline-none"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} title="Clear" className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <SlIcon library="lucide" name="x" style={{ fontSize: "16px" }} />
                </button>
              )}
            </div>
            {debouncedQuery && (
              <span className={`text-sm whitespace-nowrap font-medium ${matchIds.length ? "text-gray-600" : "text-red-400"}`}>
                {matchIds.length ? currentMatchIndex + 1 : 0}/{matchIds.length}
              </span>
            )}
            <div className="flex items-center">
              <button onClick={gotoPrevMatch} disabled={!matchIds.length} title="Previous (Shift+Enter)" className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-40">
                <SlIcon library="lucide" name="chevron-up" />
              </button>
              <button onClick={gotoNextMatch} disabled={!matchIds.length} title="Next (Enter)" className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-40">
                <SlIcon library="lucide" name="chevron-down" />
              </button>
            </div>
            <button onClick={closeSearch} title="Close (Esc)" className="p-1 text-gray-500 hover:text-gray-800">
              <SlIcon library="lucide" name="x" />
            </button>
          </>
        ) : (
          <button onClick={openSearch} className="flex items-center gap-1.5 w-full text-base text-gray-400 hover:text-gray-600 transition-colors">
            <SlIcon library="lucide" name="search" className="text-gray-400 flex-shrink-0" style={{ fontSize: "17px" }} />
            Search messages…
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0 overscroll-contain">
        {loading ? (
          <p className="text-center text-gray-400 mt-8">Loading…</p>
        ) : messages.length ? (
          messages.map((m, i) => (
            <Message
              key={m.id ?? i}
              msg={m}
              prev={messages[i - 1]}
              messages={messages}
              onReply={handleReply}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onJumpToMessage={jumpToMessage}
              isEditing={editingMessage?.id === m.id}
              searchQuery={matchIdSet.has(m.id) ? debouncedQuery : ""}
              isActiveMatch={m.id === activeMatchId}
              isJumpTarget={m.id === jumpTargetId}
              setRef={(el) => registerMessageRef(m.id, el)}
              locked={isLocked}
            />
          ))
        ) : (
          <p className="text-center text-gray-400 mt-10">No messages yet</p>
        )}
        <div ref={endRef} />
      </div>

      {/* Reply preview */}
      {replyTo && (
        <div className="mx-3 mb-1 p-2 bg-blue-50 rounded-xl border-l-4 border-blue-500 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-600">Replying to {replyTo.activity_done_by_name}</p>
            <p className="text-sm text-gray-600 truncate">{replyTo.message?.slice(0, 60)}{replyTo.message?.length > 60 ? "..." : ""}</p>
          </div>
          <button onClick={cancelReply} className="text-gray-400 hover:text-gray-600 p-1">
            <SlIcon library="lucide" name="x" />
          </button>
        </div>
      )}

      {/* Edit preview */}
      {editingMessage && (
        <div className="mx-3 mb-1 p-2 bg-green-50 rounded-xl border-l-4 border-green-500 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-600">Editing message</p>
          </div>
          <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 p-1">
            <SlIcon library="lucide" name="x" />
          </button>
        </div>
      )}

      {/* Staged chat attachment — a reference file, NOT a design submission */}
      {attachment && (
        <div className="mx-3 mb-1 p-2 bg-white rounded-xl border border-amber-300 shadow-sm flex items-center gap-3">
          {attachment.isVideo ? (
            <video src={attachment.previewUrl} className="h-16 w-24 object-cover rounded" />
          ) : (
            <img src={attachment.previewUrl} alt="attachment preview" className="h-16 w-16 object-cover rounded" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-700 flex items-center gap-1">
              <SlIcon library="lucide" name="paperclip" style={{ fontSize: "14px" }} />
              Chat attachment — {attachment.isVideo ? "video" : "image"}
            </p>
            <p className="text-sm text-gray-500">
              Shared as a reference file in this conversation. This does <b>not</b> submit the design.
            </p>
            {uploadProgress === null ? (
              <p className="text-sm text-gray-400">Press Enter or click send. Add a caption first if you like.</p>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-gray-200 rounded overflow-hidden">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="text-xs text-gray-500 tabular-nums">{uploadProgress}%</span>
              </div>
            )}
          </div>
          <button
            onClick={cancelAttachment}
            disabled={uploadProgress !== null}
            className="text-red-400 hover:text-red-600 p-1 disabled:opacity-40"
            title="Remove attachment"
          >
            <SlIcon library="lucide" name="x" />
          </button>
        </div>
      )}

      {/* Input bar — replaced by a read-only notice once the order is created */}
      {isLocked ? (
        <div className="bg-[#f0f0f0] px-3 py-3 flex items-center justify-center gap-2 border-t border-gray-300 flex-shrink-0 text-gray-500">
          <SlIcon library="lucide" name="lock" style={{ fontSize: "17px" }} />
          <span className="text-sm sm:text-base font-medium text-center">
            Order created — this conversation is locked. No further messages or uploads.
          </span>
        </div>
      ) : (
      <div className="bg-[#f0f0f0] px-3 py-2 flex gap-2 items-end border-t border-gray-300 flex-shrink-0">
        {localStorage.getItem('role') === roles.designer && canDesignerUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white shadow text-gray-600 hover:bg-gray-100 flex-shrink-0"
            title="Upload design file"
          >
            <SlIcon library="lucide" name="upload" />
          </button>
        )}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white shadow text-gray-600 hover:bg-gray-100 flex-shrink-0 disabled:opacity-60"
          title="Attach a reference file to the chat — this does NOT submit the design"
          aria-label="Attach a reference file to the chat. This does not submit the design."
        >
          <SlIcon library="lucide" name="paperclip" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            stageAttachment(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          className="flex-1 rounded-2xl border-0 px-4 py-2 focus:outline-none resize-none max-h-32 min-h-[40px] bg-white shadow-sm"
          style={{ fontSize: '18px' }}
          rows={1}
        />
        {editingMessage && (
          <button
            onClick={cancelEdit}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-500 text-white shadow hover:bg-gray-600 transition-colors flex-shrink-0"
            title="Cancel"
          >
            <SlIcon library="lucide" name="x" />
          </button>
        )}
        <button
          onClick={sendMessage}
          disabled={sending || uploadProgress !== null}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#075E54] text-white shadow hover:bg-[#128C7E] transition-colors flex-shrink-0 disabled:opacity-60"
          title="Send"
        >
          <SlIcon library="lucide" name={sending || uploadProgress !== null ? "loader" : "send"} />
        </button>
      </div>
      )}

      {/* UploadModal for manual design upload button */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        creativeId={data?.creative_id}
        creativeType={data?.ad_creative_sub_type}
      />
    </div>
  );
};

export default ChatContent;
